/* ============================================
   LeadTareasTab — el trabajo de la oportunidad

   Dos vistas de lo mismo y un interruptor:

   · TABLA (la de entrada). Se escribe directo en la grilla: el estado y las
     fechas se cambian en su celda, y la última fila es un campo vacío donde se
     tipea y entra la tarea. Es el modo de llenar.
   · KANBAN. Las mismas tareas por estado, arrastrables. Es el modo de mirar.

   Hacer clic en una fila abre la tarea DENTRO de la pestaña (`TareaDetalle`),
   no en otra ruta: el trabajo pertenece a esta oportunidad y sacar al usuario
   de ella para leer una línea no tiene sentido.

   Los valores de `estado` guardados son los de siempre (`en_progreso`,
   `completada`, `bloqueada`); lo que cambió son las etiquetas, que viven en
   `lib/tareas-estado.ts`. Ver ahí por qué.
   ============================================ */
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useApi } from '@/hooks/useApi'
import type { Tarea, EstadoTarea, PrioridadTarea } from '@/lib/types'
import { PRIORIDADES_TAREA } from '@/lib/types'
import {
  ESTADOS_TABLERO, ESTADOS_ASIGNABLES, ESTADO_TAREA_META, estaVencida, rangoDeFechas,
} from '@/lib/tareas-estado'
import { Badge } from '@/components/ui'
import TareaDetalle from './TareaDetalle'
import {
  IconPlus, IconTrash, IconList, IconKanban, IconPaperclip, IconAlertCircle,
} from '@/components/icons/Icons'

interface LeadTareasTabProps {
  leadId: number
  /** Avisa al shell para refrescar contadores. */
  onCountChange?: (total: number, pendientes: number) => void
}

type Vista = 'tabla' | 'kanban'

export default function LeadTareasTab({ leadId, onCountChange }: LeadTareasTabProps) {
  const api = useApi()
  const [tareas, setTareas] = useState<Tarea[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [vista, setVista] = useState<Vista>('tabla')
  const [abiertaId, setAbiertaId] = useState<number | null>(null)

  // Alta en línea, al pie de la tabla.
  const [nuevoTitulo, setNuevoTitulo] = useState('')
  const [creando, setCreando] = useState(false)

  const apiGet = api.get

  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      setTareas(await apiGet<Tarea[]>(`/api/tareas?lead_id=${leadId}`))
    } catch (err) {
      console.error('Error cargando tareas:', err)
      setTareas([])
    } finally {
      setLoading(false)
    }
  }, [apiGet, leadId])

  useEffect(() => { cargar() }, [cargar])

  useEffect(() => {
    const pendientes = tareas.filter(t => !ESTADO_TAREA_META[t.estado]?.terminal).length
    onCountChange?.(tareas.length, pendientes)
  }, [tareas, onCountChange])

  /** Pinta el cambio al instante y lo manda; si falla, recarga y lo dice. */
  async function parchear(tarea: Tarea, cambios: Partial<Tarea>) {
    setTareas(prev => prev.map(t => (t.id === tarea.id ? { ...t, ...cambios } : t)))
    setError(null)
    try {
      await api.put(`/api/tareas/${tarea.id}`, cambios)
    } catch (err: any) {
      console.error('Error actualizando tarea:', err)
      setError(err?.message || 'No se pudo guardar el cambio')
      cargar()
    }
  }

  async function crear() {
    const titulo = nuevoTitulo.trim()
    if (!titulo || creando) return
    setCreando(true)
    setError(null)
    try {
      const creada = await api.post<Tarea>('/api/tareas', {
        titulo,
        lead_id: leadId,
        // Nace en «Nuevo»: todavía nadie dijo que haya que hacerla ya.
        estado: 'nuevo',
        prioridad: 'media',
        tipo: 'tarea',
      })
      setTareas(prev => [...prev, creada])
      setNuevoTitulo('')
    } catch (err: any) {
      console.error('Error creando tarea:', err)
      setError(err?.message || 'No se pudo crear la tarea')
    } finally {
      setCreando(false)
    }
  }

  async function eliminar(tarea: Tarea) {
    if (!confirm(`¿Eliminar la tarea "${tarea.titulo}"?`)) return
    setError(null)
    try {
      await api.del(`/api/tareas/${tarea.id}`)
      setTareas(prev => prev.filter(t => t.id !== tarea.id))
      setAbiertaId(id => (id === tarea.id ? null : id))
    } catch (err: any) {
      console.error('Error eliminando tarea:', err)
      setError(err?.message || 'No se pudo eliminar la tarea')
    }
  }

  const abierta = useMemo(
    () => tareas.find(t => t.id === abiertaId) || null,
    [tareas, abiertaId],
  )

  if (loading) return <div className="ld-empty-tab"><p>Cargando tareas...</p></div>

  // ─── La tarea abierta se lleva la pestaña ───
  if (abierta) {
    return (
      <TareaDetalle
        leadId={leadId}
        tarea={abierta}
        onVolver={() => setAbiertaId(null)}
        onCambio={cambios => setTareas(prev => prev.map(t => (t.id === abierta.id ? { ...t, ...cambios } : t)))}
        onEliminar={eliminar}
      />
    )
  }

  return (
    <div className="ld-tareas">
      <div className="ld-tareas__barra">
        <div className="segmented" role="group" aria-label="Vista de las tareas">
          <button
            type="button"
            className={`segmented__opt${vista === 'tabla' ? ' is-active' : ''}`}
            aria-pressed={vista === 'tabla'}
            onClick={() => setVista('tabla')}
          >
            <IconList size={13} /> Tabla
          </button>
          <button
            type="button"
            className={`segmented__opt${vista === 'kanban' ? ' is-active' : ''}`}
            aria-pressed={vista === 'kanban'}
            onClick={() => setVista('kanban')}
          >
            <IconKanban size={13} /> Kanban
          </button>
        </div>
        <span className="ld-tareas__conteo">
          {tareas.length} {tareas.length === 1 ? 'tarea' : 'tareas'}
        </span>
      </div>

      {error && (
        <p className="ld-tareas__error"><IconAlertCircle size={13} /> {error}</p>
      )}

      {vista === 'tabla' ? (
        <TareasTabla
          tareas={tareas}
          nuevoTitulo={nuevoTitulo}
          creando={creando}
          onNuevoTitulo={setNuevoTitulo}
          onCrear={crear}
          onAbrir={t => setAbiertaId(t.id)}
          onParchear={parchear}
          onEliminar={eliminar}
        />
      ) : (
        <TareasKanban
          tareas={tareas}
          onAbrir={t => setAbiertaId(t.id)}
          onParchear={parchear}
        />
      )}
    </div>
  )
}

/* ─────────────────────────────────────────────
   Tabla
   ───────────────────────────────────────────── */

interface TablaProps {
  tareas: Tarea[]
  nuevoTitulo: string
  creando: boolean
  onNuevoTitulo: (v: string) => void
  onCrear: () => void
  onAbrir: (t: Tarea) => void
  onParchear: (t: Tarea, cambios: Partial<Tarea>) => void
  onEliminar: (t: Tarea) => void
}

export function TareasTabla({
  tareas, nuevoTitulo, creando, onNuevoTitulo, onCrear, onAbrir, onParchear, onEliminar,
}: TablaProps) {
  return (
    <div className="tareas-tabla">
      <table>
        <thead>
          <tr>
            <th className="tareas-tabla__col-titulo">Tarea</th>
            <th>Descripción</th>
            <th className="tareas-tabla__col-estado">Estado</th>
            <th className="tareas-tabla__col-prioridad">Prioridad</th>
            <th className="tareas-tabla__col-fechas">Fechas</th>
            <th className="tareas-tabla__col-adj" title="Adjuntos"><IconPaperclip size={12} /></th>
            <th className="col-acciones" />
          </tr>
        </thead>
        <tbody>
          {tareas.map(t => {
            const vencida = estaVencida(t)
            const rango = rangoDeFechas(t.fecha_inicio, t.fecha_limite)
            return (
              <tr key={t.id} className="tareas-tabla__fila">
                {/* El título abre; las demás celdas se editan sin abrir nada. */}
                <td>
                  <button type="button" className="tareas-tabla__abrir" onClick={() => onAbrir(t)}>
                    {t.titulo}
                  </button>
                </td>

                <td className="tareas-tabla__desc">
                  {t.descripcion
                    ? <span title={t.descripcion}>{t.descripcion}</span>
                    : <span className="cell-soft">—</span>}
                </td>

                <td>
                  {/* Un <select> y no un badge con menú: cambiar de estado es
                      lo que más se hace en esta tabla, y el select ya sabe
                      abrirse con el teclado. */}
                  <select
                    className="tareas-tabla__select"
                    value={t.estado}
                    onChange={e => onParchear(t, {
                      estado: e.target.value as EstadoTarea,
                      fecha_completada: e.target.value === 'completada'
                        ? new Date().toISOString().slice(0, 10)
                        : null,
                    })}
                  >
                    {ESTADOS_ASIGNABLES.map(e => (
                      <option key={e} value={e}>{ESTADO_TAREA_META[e].label}</option>
                    ))}
                  </select>
                </td>

                <td>
                  <select
                    className="tareas-tabla__select"
                    value={t.prioridad}
                    onChange={e => onParchear(t, { prioridad: e.target.value as PrioridadTarea })}
                  >
                    {PRIORIDADES_TAREA.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </td>

                <td>
                  {/* Las dos puntas del rango, editables en la celda. No se
                      repite el rango como texto al lado: los campos ya lo
                      dicen, y la columna no da para las tres cosas. El rojo
                      del vencimiento va sobre la fecha de cierre, que es la
                      que venció. */}
                  <div className="tareas-tabla__fechas">
                    <input
                      type="date"
                      className="tareas-tabla__fecha"
                      value={t.fecha_inicio?.slice(0, 10) || ''}
                      title="Desde"
                      aria-label={`Inicio de ${t.titulo}`}
                      onChange={e => onParchear(t, { fecha_inicio: e.target.value || null })}
                    />
                    <input
                      type="date"
                      className={`tareas-tabla__fecha${vencida ? ' tareas-tabla__fecha--vencida' : ''}`}
                      value={t.fecha_limite?.slice(0, 10) || ''}
                      title={vencida ? `Venció el ${rango}` : 'Hasta'}
                      aria-label={`Cierre de ${t.titulo}`}
                      onChange={e => onParchear(t, { fecha_limite: e.target.value || null })}
                    />
                  </div>
                </td>

                <td className="tareas-tabla__col-adj">
                  <button type="button" className="btn-icon-mini" onClick={() => onAbrir(t)} title="Ver adjuntos">
                    <IconPaperclip size={12} />
                  </button>
                </td>

                <td className="col-acciones">
                  <button
                    type="button"
                    className="btn-icon-mini btn-icon-mini--danger"
                    onClick={() => onEliminar(t)}
                    title="Eliminar"
                  >
                    <IconTrash size={12} />
                  </button>
                </td>
              </tr>
            )
          })}

          {/* La fila de alta vive DENTRO de la tabla, como en una hoja de
              cálculo: escribir y pulsar Enter es todo el flujo. Un botón
              «Nueva tarea» arriba obliga a buscar dónde escribir. */}
          <tr className="tareas-tabla__alta">
            <td colSpan={7}>
              <div className="tareas-tabla__alta-fila">
                <IconPlus size={13} />
                <input
                  type="text"
                  className="tareas-tabla__alta-input"
                  placeholder="Nueva tarea — escribí y pulsá Enter"
                  value={nuevoTitulo}
                  onChange={e => onNuevoTitulo(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') onCrear() }}
                  disabled={creando}
                />
                {nuevoTitulo.trim() && (
                  <button type="button" className="btn btn-sm btn-primary" onClick={onCrear} disabled={creando}>
                    {creando ? 'Creando...' : 'Agregar'}
                  </button>
                )}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

/* ─────────────────────────────────────────────
   Kanban
   ───────────────────────────────────────────── */

export function TareasKanban({ tareas, onAbrir, onParchear }: {
  tareas: Tarea[]
  onAbrir: (t: Tarea) => void
  onParchear: (t: Tarea, cambios: Partial<Tarea>) => void
}) {
  const [sobre, setSobre] = useState<EstadoTarea | null>(null)

  function soltar(estado: EstadoTarea, e: React.DragEvent) {
    e.preventDefault()
    setSobre(null)
    const id = Number(e.dataTransfer.getData('text/plain'))
    const tarea = tareas.find(t => t.id === id)
    if (!tarea || tarea.estado === estado) return
    onParchear(tarea, {
      estado,
      fecha_completada: estado === 'completada' ? new Date().toISOString().slice(0, 10) : null,
    })
  }

  /*
    Las canceladas no tienen columna, así que se cuentan aparte y se avisan al
    pie. Esconderlas sin decir cuántas son deja a quien canceló una creyendo
    que la perdió; la tabla sigue mostrándolas enteras.
  */
  const canceladas = tareas.filter(t => t.estado === 'cancelada').length

  return (
    <>
    <div className="tareas-kanban">
      {ESTADOS_TABLERO.map(estado => {
        const meta = ESTADO_TAREA_META[estado]
        const deLaColumna = tareas.filter(t => (t.estado || 'pendiente') === estado)
        return (
          <section
            key={estado}
            className={`tareas-kanban__col${sobre === estado ? ' is-sobre' : ''}`}
            onDragOver={e => { e.preventDefault(); setSobre(estado) }}
            onDragLeave={() => setSobre(s => (s === estado ? null : s))}
            onDrop={e => soltar(estado, e)}
          >
            <header className="tareas-kanban__cabecera">
              <span className="tareas-kanban__punto" style={{ background: meta.color }} />
              <span className="tareas-kanban__titulo">{meta.label}</span>
              <span className="tareas-kanban__conteo">{deLaColumna.length}</span>
            </header>

            <div className="tareas-kanban__pila">
              {deLaColumna.map(t => {
                const rango = rangoDeFechas(t.fecha_inicio, t.fecha_limite)
                return (
                  <article
                    key={t.id}
                    className="tareas-kanban__carta"
                    draggable
                    onDragStart={e => e.dataTransfer.setData('text/plain', String(t.id))}
                    onClick={() => onAbrir(t)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter') onAbrir(t) }}
                  >
                    <span className="tareas-kanban__carta-titulo">{t.titulo}</span>
                    <div className="tareas-kanban__carta-pie">
                      <Badge variant={t.prioridad === 'urgente' || t.prioridad === 'alta' ? 'warning' : 'neutral'}>
                        {t.prioridad}
                      </Badge>
                      {rango && (
                        <span className={`tareas-kanban__fecha${estaVencida(t) ? ' tareas-kanban__fecha--vencida' : ''}`}>
                          {rango}
                        </span>
                      )}
                    </div>
                  </article>
                )
              })}

              {deLaColumna.length === 0 && (
                <p className="tareas-kanban__vacio">—</p>
              )}
            </div>
          </section>
        )
      })}
    </div>

    {canceladas > 0 && (
      <p className="tareas-kanban__canceladas">
        {canceladas === 1 ? '1 tarea cancelada' : `${canceladas} tareas canceladas`}, fuera del tablero.
        Las ves en la vista de tabla.
      </p>
    )}
    </>
  )
}
